import Enums from '@satellite/enums';
import CanadianProvincesEnum from '../enums/CanadianProvincesEnum';
import { isObject, isDefined } from 'class-validator';
import SizingEnum from '@satellite/enums/SizingEnum';
import { isString, isFunction } from 'lodash';
import {
  truncateString,
  transformForUserFriendlyTimezone,
  formatDateTimeWithMilitarySupport,
  t,
  upperFirst,
  fileKeyToDownloadUrl
} from '@nova/core';
import { DateTime } from 'luxon';

const { USStatesEnum, RenderModeEnum } = Enums;

/**
 * Use this to prevent it from being cleared from localStorage on logout
 */
const NOVA_LOCAL_STORAGE_PERSISTED_KEY_PREFIXES = ['nova_gate_feed_'];

export function isMobileDevice() {
  return /Android|webOS|iPhone|iPod|iPad|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

const AXIOS_ERROR_NAME = 'AxiosError';
export const LAST_CRUD_ACTION_KEY = 'lastCrudAction';

export function isAxiosError(error) {
  const name = error?.name ?? '';
  return name === AXIOS_ERROR_NAME;
}

export function isMobile() {
  const currentRenderMode = localStorage.getItem('renderMode');
  return isMobileDevice() && currentRenderMode !== RenderModeEnum.DESKTOP;
}

export function getTimeInWarehouseTimezone(
  appointment,
  format12,
  format24,
  isMilitaryTimeEnabled,
  locale = 'en'
) {
  return transformForUserFriendlyTimezone(
    formatDateTimeWithMilitarySupport(
      appointment.utcStart ?? appointment.start,
      appointment.dock.warehouse.timezone,
      format12,
      isMilitaryTimeEnabled,
      format24,
      locale
    ),
    appointment.dock.warehouse.timezone
  );
}

export default {
  scrollToElement(element, offset = 45) {
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  },
  // TODO (API DEPENDENT) this function really shouldn't need to exist
  // Hopefully we can relax the API in the future so that will except days with an empty array
  filterSchedule(schedule) {
    return Object.keys(schedule)
      .filter(k => k === 'version' || schedule[k].length > 0)
      .reduce((a, k) => ({ ...a, [k]: schedule[k] }), {});
  },
  sortObjectArray(arr, sortBy, desc = false) {
    arr.sort((a, b) =>
      (desc && a[sortBy] < b[sortBy]) || (!desc && a[sortBy] > b[sortBy]) ? 1 : -1
    );
  },
  sortObjectByKeys(obj) {
    return Object.keys(obj)
      .sort()
      .reduce((o, k) => {
        o[k] = obj[k];
        return o;
      }, {});
  },
  /** @deprecated Use Luxon DateTime.fromFormat() directly */
  formatStringToDate(string, inputFormat, outputFormat) {
    const dt = DateTime.fromFormat(string, inputFormat);
    return dt.isValid ? dt.toFormat(outputFormat) : 'Invalid date';
  },
  getAmericanStateSelectOptions() {
    return this.InvertObjectKeyValuePairs(USStatesEnum);
  },
  getCanadianProvinceSelectOptions() {
    return this.InvertObjectKeyValuePairs(CanadianProvincesEnum);
  },
  InvertObjectKeyValuePairs(object) {
    const invertedObject = {};
    Object.keys(object).forEach(key => {
      invertedObject[object[key]] = key;
    });

    return invertedObject;
  },
  removeCursorTextListener($el, textElId) {
    const cursorElement = document.getElementById(textElId);
    if (cursorElement) {
      cursorElement.remove();
    }

    $el.removeEventListener('mouseover', () => {});
    $el.removeEventListener('mouseout', () => {});
    $el.removeEventListener('mousemove', () => {});
  },
  addCursorTextListener($el, text, textElId) {
    let $cursorTextEl = document.createElement('div');
    $cursorTextEl.className = 'cursorText';
    $cursorTextEl.id = textElId;
    $cursorTextEl.innerHTML = text;
    $el.appendChild($cursorTextEl);

    function moveCursor(e) {
      if (!e) {
        e = window.event;
      }
      $cursorTextEl.style.left = e.clientX + 30 + 'px';
      $cursorTextEl.style.top = e.clientY + 'px';
    }
    function showCursorText() {
      if ($el.classList.contains('disabled') && !$el.classList.contains('clickable')) {
        $cursorTextEl.style.display = 'block';
      }
    }
    function hideCursorText() {
      $cursorTextEl.style.display = 'none';
    }

    $el.onmouseover = showCursorText;
    $el.onmouseout = hideCursorText;
    $el.onmousemove = moveCursor;
  },
  registerComponentsInDirectory(requireComponent, VuePrototype) {
    requireComponent.keys().forEach(fileName => {
      // Get component config
      const componentConfig = requireComponent(fileName);

      // Get PascalCase name of component
      const componentName = upperFirst(
        // Gets the file name regardless of folder depth
        fileName
          .split('/')
          .pop()
          .replace(/\.\w+$/, '')
      );

      // Register component globally
      VuePrototype.component(
        componentName,
        // Look for the component options on `.default`, which will
        // exist if the component was exported with `export default`,
        // otherwise fall back to module's root.
        componentConfig.default || componentConfig
      );
    });

    return VuePrototype;
  },
  makeTimeDiff(start, end, timezone, unit = 'milliseconds', decimals = 0) {
    const startDt = DateTime.fromISO(
      typeof start === 'string' ? start : new Date(start).toISOString(),
      { zone: timezone }
    ).startOf('minute');
    const endDt = DateTime.fromISO(typeof end === 'string' ? end : new Date(end).toISOString(), {
      zone: timezone
    }).startOf('minute');

    return endDt.diff(startDt, unit)[unit].toFixed(decimals);
  },
  getReadableDuration(startTime, endTime, timezone, label = '') {
    let readableDuration = '';
    const diff = parseInt(this.makeTimeDiff(startTime, endTime, timezone));

    if (diff > 0) {
      const totalMinutes = Math.floor(diff / 60000);
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;
      readableDuration += days ? `${days}` : '';
      readableDuration += days ? (days > 1 ? ' days ' : ' day ') : '';
      readableDuration += hours ? `${hours}` : '';
      readableDuration += hours ? (hours > 1 ? ' hrs ' : ' hr ') : '';
      readableDuration += minutes ? `${minutes} min` : '';
      readableDuration += label ? ` ${label}` : '';
    } else {
      if (label.toLowerCase().trim() === 'dwell') {
        if (diff === 0) {
          readableDuration = '0 min dwell';
        }
      } else if (label.toLowerCase().trim() === 'late') {
        readableDuration = diff === 0 ? 'On time' : 'Early';
      } else if (label.toLowerCase().trim() === 'wait') {
        if (diff === 0) {
          readableDuration = 'No wait time';
        }
      }
    }

    return readableDuration.trim().replace(/  +/g, ' ');
  },
  clearLocalStorage(items) {
    const keepKeys = key =>
      NOVA_LOCAL_STORAGE_PERSISTED_KEY_PREFIXES.some(prefix => key.startsWith(prefix));

    if (items?.length) {
      items.filter(item => !keepKeys(item)).forEach(item => localStorage.removeItem(item));
    } else {
      Object.keys(localStorage)
        .filter(key => !keepKeys(key))
        .forEach(key => localStorage.removeItem(key));
    }
  },
  clearSessionStorage(items) {
    if (items?.length) {
      items.map(item => sessionStorage.removeItem(item));
    } else {
      sessionStorage.clear();
    }
  },
  getCustomTagData(customTags = [], name = '') {
    customTags = customTags ?? [];
    return customTags.find(ct => ct.name === name);
  },
  makeTagObject(customTags = [], tag) {
    customTags = customTags ?? [];
    const name = typeof tag === 'string' ? tag : tag.name;
    const customTag = this.getCustomTagData(customTags, name);

    return customTag ?? { name, color: null, textColor: null };
  },
  getReadableChildDockName(parentDock) {
    return `${t('grid.labels.parallelCapacityPrefix')}: ${truncateString(parentDock.name, 20)}`;
  },
  /**
   * Deep merge two objects.
   * https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge#answer-37164538
   */
  mergeDeep(target, source) {
    const output = { ...{}, ...target };
    if (isObject(target) && isObject(source)) {
      Object.keys(source).forEach(key => {
        if (isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.mergeDeep(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  },
  // This is to push information onto the datalayer which is used by google tag manager and google analytics
  pushMeToDataLayer(me) {
    const { companyId, email, firstName, lastName, orgId, orgName, role, id } = me;

    window.dataLayer.push({
      user: {
        id,
        companyId,
        email,
        firstName,
        lastName,
        orgId,
        orgName,
        role
      }
    });
  },
  deleteConfirmationInputText(count, entity) {
    return t('lists.common.messages.yesIAmAwareEntityCountWillBeDeleted', { count, entity });
  },
  getActiveDocksFromWarehouse(warehouse) {
    return warehouse?.docks?.filter(dock => dock.isActive);
  },
  getLoadTypesFromActiveDocks(docks) {
    let selectedLoadTypes = [];
    docks?.forEach(dock => {
      if (dock.isActive) {
        selectedLoadTypes = [...selectedLoadTypes, ...dock.loadTypeIds];
      }
    });
    return Array.from(new Set(selectedLoadTypes));
  },
  getNumberGroupsFromString(str) {
    return str?.match(/[0-9.]*/g) ?? [];
  },
  getSizeUnitFromString(str) {
    return str?.match(/[a-zA-Z%]+/g) ?? [];
  },
  stringOnlyContainsNumbers(str) {
    return /[0-9.]*/.test(str);
  },
  stringContainsNoNumbers(str) {
    return !/\d/.test(str);
  },
  /**
   * This is ugly but until I find a better way to control the shadow dom element styles for RARE style applications,
   * This will have to do...
   * @param shadowRootEl The shadow-root element
   * @param shadowDomClass Selector for element inside the shadow dom we apply the styles to
   * @param styles string of semicolon delimited styling rules
   */
  appendStyleToShadowDomEl(shadowRootEl, shadowDomSelector, styles) {
    let style = document.createElement('style');
    style.innerHTML = `${shadowDomSelector} {${styles}}`;
    shadowRootEl.shadowRoot?.appendChild(style);
  },
  appendStylesToShadowDomEls(shadowDomEls, shadowDomSelector, styles) {
    shadowDomEls.forEach(el => this.appendStyleToShadowDomEl(el, shadowDomSelector, styles));
  },
  getLargerAreaOfAddress({ city, state, country, zip }, includeZip = true) {
    let largerArea = `${city}, ${state ?? country}`;
    if (includeZip) {
      largerArea += ` ${zip}`;
    }
    return largerArea;
  },
  /**
   * Parses the sizeInput to get the value/unit
   */
  computeSize(sizeInput) {
    let value;
    let unit;
    let size;
    if (this.stringContainsNoNumbers(sizeInput)) {
      // Supports 'small', 'default', and 'large'
      size = SizingEnum[sizeInput];
    } else {
      // Supports an manual size and unit i.e. 10px or 2.5rem
      size = sizeInput;
    }
    value = this.getSizeValue(size);
    unit = this.getSizeUnit(size);
    if (this.isSizeValid(value, unit)) {
      return {
        sizeValue: value,
        sizeUnit: unit
      };
    } else {
      return;
    }
  },
  isSizeValid(value, unit) {
    return this.stringOnlyContainsNumbers(value) && this.stringContainsNoNumbers(unit);
  },
  getSizeValue(size) {
    return this.getNumberGroupsFromString(size)?.[0];
  },
  getSizeUnit(size) {
    return this.getSizeUnitFromString(size)?.[0] ?? 'px';
  },
  validateValuesAgainstRules(val, rules) {
    const errorBucket = [];
    for (let index = 0; index < rules.length; index++) {
      const rule = rules[index];
      const valid = typeof rule === 'function' ? rule(val) : rule;
      if (valid === false || typeof valid === 'string') {
        errorBucket.push(valid || '');
      } else if (typeof valid !== 'boolean') {
        console.error(
          `Rules should return a string or boolean, received '${typeof valid}' instead`,
          this
        );
      }
    }
    return errorBucket;
  },
  /**
   * This allows you to attach an element to another element.  It defaults to below, but depending on the placement
   * and if it overflows the containerEl, it will reposition to stay inside
   * @param el The element being attached to
   * @param elToAttach The element to be attached
   * @param containerEl The container - if none provided, a dialog (if inside dialog) or window will be the fallback
   * @param elToAttachWidth Ability to give a specific width rather than rely on the element width from JS
   * @param padding Padding to be applied when repositioned
   */
  attachElToEl({ el, elToAttach, containerEl = null, elToAttachWidth = null, padding = 5 }) {
    const elCoords = el.getBoundingClientRect();
    const windowDimensions = {
      width: window.innerWidth,
      height: window.innerHeight,
      left: 0,
      right: window.innerWidth,
      top: 0,
      bottom: window.innerHeight
    };
    elToAttachWidth = elToAttachWidth ?? elToAttach.innerWidth;

    // Container el will be what is provided via the args with a fallback of a dialog, and finally the window dimensions
    containerEl = containerEl ?? el.closest('m-dialog')?.shadowRoot?.querySelector('dialog');
    const boundingClient = containerEl ? containerEl.getBoundingClientRect() : windowDimensions;

    // Overflow checks
    const rightOverflowPx = elCoords.left + elToAttachWidth - boundingClient.right;
    const bottomOverflowPx = boundingClient.bottom - (elCoords.bottom + elToAttachWidth);
    const hasTopOverflow = elCoords.top - elToAttachWidth < boundingClient.top;

    // Attach element, then adjust based on overflow checks
    elToAttach.style.top = `${elCoords.bottom}px`;
    elToAttach.style.left = `${elCoords.left}px`;

    if (rightOverflowPx > 0) {
      elToAttach.style.left = `${elCoords.left - rightOverflowPx - padding}px`;
    }
    if (bottomOverflowPx < 0) {
      if (hasTopOverflow) {
        elToAttach.style.top = `${padding}px`;
      } else {
        elToAttach.style.top = `${elCoords.top - elToAttachWidth - padding}px`;
      }
    }
  },
  loadObjectFromLocalStorage(key) {
    let data = {};
    if (localStorage.getItem(key)) {
      data = JSON.parse(localStorage.getItem(key));
    }

    return data;
  },
  updateObjectInLocalStorage(key, val) {
    const keyParts = key.split('.');
    const currentData = { ...this.loadObjectFromLocalStorage(keyParts[0]) };
    const data = setNestedObjectData(currentData, keyParts, val);

    localStorage.setItem(keyParts[0], JSON.stringify(data));

    function setNestedObjectData(data, keyParts, val) {
      let context = data;
      keyParts = keyParts.length > 1 ? keyParts.slice(1) : keyParts;
      keyParts.forEach((key, index) => {
        if (index + 1 === keyParts.length) {
          context[key] = val;
        } else {
          if (typeof context[key] === 'undefined') {
            context[key] = {};
          }
          context = context[key];
        }
      });
      return context;
    }
  }
};

export function propValidatorHelper({
  value,
  isValid,
  componentName,
  propName,
  errorMessage,
  docsLink
}) {
  // TODO: Add ability to see incorrect props given
  if (!isValid) {
    console.error(
      `Component: ${componentName}.vue\nProp Name: ${propName} ${
        errorMessage ? `- ${errorMessage}` : ''
      }\nCurrent prop value: ${JSON.stringify(value)}.\nSee docs - ${
        docsLink ?? `http://localhost:6060/#${componentName.toLowerCase()}`
      }`
    );
  }

  return isValid;
}

export function validateV2DropDownOptionsProp(prop, componentName) {
  let isValid = true;
  const hasStrings = prop.every(val => isString(val));
  if (!hasStrings) {
    // Expects following schema: [ { label: 'option label', value: 'option value' } ]
    isValid = prop.every(obj => Object.keys(obj).every(key => ['label', 'value'].includes(key)));
  }

  return propValidatorHelper({
    prop,
    isValid,
    componentName: componentName,
    propName: 'options',
    message: ''
  });
}

export function storeBooleanInLocalStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function retrieveBooleanFromLocalStorage(key) {
  const storedValue = localStorage.getItem(key);
  return storedValue === null ? null : JSON.parse(storedValue);
}

export function customLogoPath(warehouse, s3BaseUrl) {
  // Use warehouseLogo first, then orgLogo
  const orgLogo = warehouse?.org?.settings?.customLogo;
  const warehouseLogo = warehouse?.settings?.customLogo;
  const logo = warehouseLogo ?? orgLogo;

  return logo ? fileKeyToDownloadUrl(s3BaseUrl, logo) : null;
}

export function getEventsFromAttrs(attrs) {
  const events = {};
  Object.keys(attrs).forEach(attr => {
    if (attr.startsWith('on')) {
      events[attr] = attrs[attr];
    }
  });
  return events;
}

export function removeEventsFromAttrs(attrs) {
  const events = {};
  Object.keys(attrs).forEach(attr => {
    if (!attr.startsWith('on')) {
      events[attr] = attrs[attr];
    }
  });
  return events;
}

/**
 * Calculates the midpoint of a given Luxon Interval.
 *
 * @param {Interval} interval - The Luxon Interval object.
 * @returns {DateTime} - The midpoint DateTime of the interval.
 * @throws {Error} - If the interval is invalid.
 */
export function calculateIntervalMidpoint(interval) {
  if (!interval.isValid) {
    throw new Error('Invalid Interval: Start must be before End');
  }

  const durationInMinutes = interval.length('minutes');
  const midpoint = interval.start.plus({ minutes: durationInMinutes / 2 });
  return midpoint;
}

/**
 * Scrolls the first element with the error class into view.
 *
 * @param {container} html element - The container of the errors, if any.
 * @param {errorClass} html string - The class the first error will have on it.
 * @returns void
 */
export function scrollFirstErrorIntoView(container = null, errorClass = 'v-input--error') {
  const elements = container
    ? container.getElementsByClassName(errorClass)
    : document.getElementsByClassName(errorClass);
  if (elements?.length > 0) {
    elements[0].scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
}

/**
 * Runs the validation on v-form components and returns the validity.
 * Will also scroll to the first error unless told not to
 *
 * @param {formRef} v-form ref - The form we are validating.
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating if the form is valid.
 */
export async function isFormValid(formRef, scrollToError = true) {
  if (!isDefined(formRef) || !isFunction(formRef.validate)) {
    return false;
  }

  const isValid = Boolean((await formRef.validate())?.valid);

  if (!isValid && scrollToError && formRef?.$el) {
    scrollFirstErrorIntoView(formRef.$el);
  }
  return isValid;
}

export function waitForElement(selector) {
  return new Promise(resolve => {
    const checkExist = setInterval(() => {
      const element = document.querySelector(selector);
      if (element) {
        clearInterval(checkExist);
        resolve(element);
      }
    }, 100); // Check every 100ms
  });
}

// Create a new JS Date object in the given timezone
export function getJSDateInTimezone(jsDate, timezone) {
  const dtInTimezone = DateTime.fromJSDate(jsDate).setZone(timezone);
  return buildJSDateFromLuxon(dtInTimezone);
}

// Manually builds a JS Date object from a Luxon DateTime object
export function buildJSDateFromLuxon(dt, ignoreTime = false) {
  let dateTime = {
    year: dt.year,
    month: dt.month,
    day: dt.day
  };
  if (!ignoreTime) {
    dateTime = { ...dateTime, hour: dt.hour, minute: dt.minute };
  }
  return DateTime.fromObject(dateTime).toJSDate();
}

// TODO this is a temporary solution to date handling from the Vuetify datepicker when we just need to use the date -
// clicked, without any time manipulation. We have found a better solution for handling dates from the Vuetify picker -
// and we will be implementing it soon, when we do, this method can be removed.
export function buildDateDashedStringFromJSDate(jsDate) {
  const year = jsDate.getFullYear();
  const month = String(jsDate.getMonth() + 1).padStart(2, '0'); // Ensure 2-digit format
  const day = String(jsDate.getDate()).padStart(2, '0'); // Ensure 2-digit format

  return `${year}-${month}-${day}`;
}

/**
 * Converts a date string in YYYY-MM-DD format to a JS Date object preserving the date
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Date} - JS Date object with the same date
 */
export function createDateFromString(dateString) {
  if (!dateString) return null;

  // Append T00:00:00 to ensure it's interpreted at midnight in local timezone
  return new Date(`${dateString}T00:00:00`);
}

export function getRouteTitle(title) {
  return typeof title === 'function' ? title() : title;
}

export function getObjectAsRequestSearchString(obj) {
  return `s=${encodeURIComponent(JSON.stringify(obj))}`;
}
